import React, { useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import { Amplify } from 'aws-amplify';
import { Authenticator, ThemeProvider } from '@aws-amplify/ui-react';
import { createTheme, defaultTheme } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import './custom-styles.css';
import awsconfig from './aws-exports';
import { SessionProvider } from './context/SessionContext';
import diagramLogo from './assets/diagram-logo.svg';
import HomePage from './pages/HomePage';
import DiagramGenerator from './pages/DiagramGenerator';
import PlantUMLService from './pages/PlantUMLService';
import MermaidService from './pages/MermaidService';
import ArchitectureDesign from './pages/ArchitectureDesign';
import ProcessFlows from './pages/ProcessFlows';
import Gather from './pages/Gather';
import Review from './pages/Review';
import Artifacts from './pages/Artifacts';
import Generation from './pages/Generation';

// Debug: Log current location
console.log('🔍 Current location:', window.location.href, 'Port:', window.location.port);

// Configure Amplify with dynamic port detection
const cfg = { ...awsconfig };
if (cfg.oauth && window.location.hostname === 'localhost') {
  const currentPort = window.location.port || '3000';
  cfg.oauth.redirectSignIn = `http://localhost:${currentPort}/`;
  cfg.oauth.redirectSignOut = `http://localhost:${currentPort}/`;
}
Amplify.configure(cfg);

const myDarkTheme = createTheme({
  ...defaultTheme,
  name: 'diagramloom-dark-theme',
  tokens: {
    colors: {
      brand: {
        primary: {
          80: '#FF9900',
        },
      },
      background: {
        primary: '#121212',
      },
      font: {
        primary: {
          value: 'Arial',
        },
      },
    },
  },
});



function App() {
  const logoRef = useRef(null);
  
  // Show app interface when explicitly requested or OAuth callback detected
  const hasOAuthCallback = window.location.search.includes('code=') && window.location.search.includes('state=');
  const isExplicitApp = window.location.search.includes('app=true');
  const isAppSubdomain = window.location.hostname === 'app.diagramloom.ai' || 
    (window.location.hostname === 'localhost' && (isExplicitApp || hasOAuthCallback));
  
  // Redirect to app subdomain if trying to access app from www
  if (window.location.hostname === 'www.diagramloom.ai' && window.location.search.includes('app=true')) {
    console.log('🔄 Redirecting from www to app subdomain');
    window.location.href = 'https://app.diagramloom.ai/';
    return null; // Prevent rendering while redirecting
  }

  if (isAppSubdomain) {
    // Protected app routes
    return (
      <ThemeProvider theme={myDarkTheme}>
        <Authenticator
          socialProviders={['google', 'amazon', 'apple']}
          components={{
            Header() {
              return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#1E1E1E', padding: '10px' }}>
                  <div 
                    onClick={() => {
                      if (window.location.hostname === 'localhost') {
                        window.location.href = `http://localhost:${window.location.port || '3000'}/`;
                      } else if (window.location.hostname === 'app.diagramloom.ai') {
                        window.location.href = 'https://www.diagramloom.ai/';
                      } else {
                        window.location.href = 'https://www.diagramloom.ai/';
                      }
                    }}
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', cursor: 'pointer' }}
                  >
                    <img
                      src={diagramLogo}
                      alt="DiagramLoom Logo"
                      id="diagramLogo"
                      style={{ width: '50px', marginRight: '10px' }}
                      ref={logoRef}
                    />
                    <div>
                      <h1 style={{ color: '#FF9900', margin: 0, textAlign: 'center' }}>DiagramLoom</h1>
                      <p style={{ color: '#FFFFFF', margin: 0, textAlign: 'center' }}>Generative Design Assistant</p>
                    </div>
                  </div>
                </div>
              );
            },
          }}
        >
          {({ signOut, user }) => (
            <SessionProvider>
              <Router>
                <ScrollToTop />
                <Routes>
                  <Route path="/" element={<ArchitectureDesign user={user} />} />
                  <Route path="/diagram-generator" element={<DiagramGenerator user={user} />} />
                  <Route path="/plantuml-service" element={<PlantUMLService user={user} />} />
                  <Route path="/mermaid-service" element={<MermaidService user={user} />} />
                  <Route path="/architecture-design" element={<ArchitectureDesign user={user} />} />
                  <Route path="/process-flows" element={<ProcessFlows user={user} />} />
                  <Route path="/gather" element={<Gather />} />
                  <Route path="/review" element={<Review />} />
                  <Route path="/artifacts" element={<Artifacts />} />
                  <Route path="/generation" element={<Generation />} />
                  <Route path="/project/:sessionId/gather" element={<Gather />} />
                  <Route path="/project/:sessionId/review" element={<Review />} />
                  <Route path="/project/:sessionId/artifacts" element={<Artifacts />} />
                  <Route path="/project/:sessionId/generation" element={<Generation />} />
                </Routes>
              </Router>
            </SessionProvider>
          )}
        </Authenticator>
      </ThemeProvider>
    );
  }

  // Public landing page
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
      </Routes>
    </Router>
  );
}

export default App;